import { useRef, useEffect } from 'react';

import { type PaymentMethod } from '@order/features-ordering';

/**
 * The {selectedPaymentMethod} is controlled by Stripe, so we don't have access to its callback or a "onPaymentMethodChanged", we can only sync via {useEffect}.
 * If a payment method is changed, it must close the changing payment modal.
 * If it was removed, the list must be refetched as it would contain the stale one.
 */
export const useUpdatePaymentListOnChange = (props: Props) => {
  const {
    isShowingModal,
    selectedPaymentMethod,
    paymentMethods,
    onPaymentMethodChanged,
    onPaymentMethodRemoved,
  } = props;

  const previousSelectedPayment = useRef(selectedPaymentMethod);
  const paymentMethodsRef = useRef(paymentMethods);
  const isShowingModalRef = useRef(isShowingModal);
  const onPaymentMethodChangedRef = useRef(onPaymentMethodChanged);
  const onPaymentMethodRemovedRef = useRef(onPaymentMethodRemoved);

  const hasChangedPaymentMethod = previousSelectedPayment.current !== selectedPaymentMethod;
  paymentMethodsRef.current = paymentMethods;
  previousSelectedPayment.current = selectedPaymentMethod;
  isShowingModalRef.current = isShowingModal;
  onPaymentMethodChangedRef.current = onPaymentMethodChanged;
  onPaymentMethodRemovedRef.current = onPaymentMethodRemoved;

  useEffect(() => {
    if (!hasChangedPaymentMethod || !isShowingModalRef.current) return;

    onPaymentMethodChangedRef.current();

    const paymentMethod = previousSelectedPayment.current;
    const paymentMethodList = paymentMethodsRef.current;

    const wasPaymentMethodRemoved = !paymentMethod;
    const wasListAlreadyEmpty = paymentMethodList.length === 0;

    if (!wasPaymentMethodRemoved || wasListAlreadyEmpty) return;

    onPaymentMethodRemovedRef.current();
  }, [hasChangedPaymentMethod]);
};

// ─── Type ────────────────────────────────────────────────────────────────────

type Props = {
  isShowingModal: boolean;
  selectedPaymentMethod: string | undefined;
  paymentMethods: readonly PaymentMethod[];
  onPaymentMethodChanged: () => void;
  onPaymentMethodRemoved: () => void;
};
